/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { IITSEcom2RetailStore, ILAC_AuthToken } from '../custom-actions/DataServiceEntities.g';
import { lAC_ITSGetEcom2RetailStoresAsync } from '../custom-actions/DataActionExtension.g';
import { refreshToken } from '../utils/authService';

/**
 * GetEcom2RetailStores Input Action
 */
export class GetEcom2RetailStoresInput implements Msdyn365.IActionInput {
    public authTokenCookie: ILAC_AuthToken;

    constructor(authTokenCookie: ILAC_AuthToken) {
        this.authTokenCookie = authTokenCookie;
    }

    // a cache object type and an appropriate cache key
    public getCacheKey = () => `GETECOM2RETAILSTORES`;
    public getCacheObjectType = () => 'GETECOM2RETAILSTORES';
    public dataCacheType = (): Msdyn365.CacheType => 'application';
}

const isUnauthorized = (error: unknown): boolean =>
    (error as { data?: { ErrorResourceId?: string } } | undefined)?.data?.ErrorResourceId === '401';

/**
 * Throws when the store list could not be read, rather than answering with an empty one. A resolved value
 * is written to the action cache under this input's key, which is a single key for the whole application,
 * so an empty list answered on a failed call would leave every guest unable to reach a suite until that
 * entry expires. A rejection is not cached.
 */
async function getEcom2RetailStoresAsync(input: GetEcom2RetailStoresInput, ctx: Msdyn365.IActionContext): Promise<IITSEcom2RetailStore[]> {
    const authToken = input.authTokenCookie;

    if (!authToken?.IntuitDomeAccountId || !authToken.Token || !authToken.RefreshToken) {
        throw new Error('LAC_ITSGetEcom2RetailStores was asked for without an authenticated session');
    }

    try {
        return await lAC_ITSGetEcom2RetailStoresAsync({ callerContext: ctx }, authToken);
    } catch (error) {
        if (!isUnauthorized(error)) {
            throw error;
        }

        const refreshedToken = await refreshToken(ctx);
        if (!refreshedToken) {
            throw error;
        }

        return lAC_ITSGetEcom2RetailStoresAsync({ callerContext: ctx }, refreshedToken);
    }
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IITSEcom2RetailStore[]>>getEcom2RetailStoresAsync,
    id: 'GetEcom2RetailStores'
});
